import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography, createFilterOptions } from '@mui/material';
import { fmtCompact } from '../format';
import { isCancelled, journalApi } from '../journalApi';
import { JournalFilters, JournalPharmacy, OptionField, OptionItem, OptionPick } from '../types';

const OPTIONS_DELAY_MS = 250;

/** Краткое описание выбора: до двух названий, дальше - «+N» */
export const summary = (names: string[]): string => (
  names.length <= 2 ? names.join(', ') : `${names[0]}, ${names[1]} +${names.length - 2}`
);

interface OptionProps {
  field: OptionField;
  label: string;
  hint: string;
  value: OptionPick | null;
  /** Применённые фильтры: подбор значений учитывает все, кроме своего поля */
  filters: JournalFilters;
  onChange: (pick: OptionPick | null) => void;
  minWidth?: number;
  fullWidth?: boolean;
}

const toItem = (pick: OptionPick): OptionItem => ({ value: pick.value, label: pick.label, extra: null, count: 0, sum: 0, pharmacies: 0 });

/**
 * Выбор одного значения фильтра с подбором на сервере (/options): каскадно по остальным фильтрам,
 * у каждого варианта - число записей и сумма, чтобы было видно «вес» значения ещё до выбора.
 */
export const OptionFilter: React.FC<OptionProps> = ({ field, label, hint, value, filters, onChange, minWidth = 220, fullWidth }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<OptionItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const ctrl = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      journalApi.options(field, query.trim(), filters, ctrl.signal)
        .then(res => { setOptions(res); setLoading(false); })
        .catch(err => {
          if (isCancelled(err)) return;
          setOptions([]);
          setLoading(false);
        });
    }, OPTIONS_DELAY_MS);
    return () => { window.clearTimeout(timer); ctrl.abort(); };
  }, [open, query, field, filters]);

  const selected = useMemo(() => (value ? toItem(value) : null), [value]);

  return (
    <Autocomplete<OptionItem, false, false, false>
      size="small" fullWidth={fullWidth} sx={{ minWidth, flex: fullWidth ? undefined : '1 1 220px', maxWidth: fullWidth ? undefined : 360 }}
      open={open} onOpen={() => setOpen(true)} onClose={() => { setOpen(false); setQuery(''); }}
      options={options} value={selected} loading={loading}
      filterOptions={x => x}
      onChange={(_, v) => onChange(v ? { value: v.value, label: v.label } : null)}
      onInputChange={(_, v, reason) => { if (reason === 'input') setQuery(v); }}
      getOptionLabel={o => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      renderOption={(props, o) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
        return (
          <li key={key} {...rest}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', minWidth: 0 }}>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.3 }} noWrap title={o.label}>{o.label}</Typography>
                {o.extra && <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }} noWrap>{o.extra}</Typography>}
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 600 }}>{fmtCompact(o.sum)} ₸</Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                  {o.count.toLocaleString('ru-RU')} зап. · {o.pharmacies} апт.
                </Typography>
              </Box>
            </Box>
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params} label={label} placeholder={value ? '' : hint}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && open ? <CircularProgress color="inherit" size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      noOptionsText={loading ? 'Загрузка…' : 'Ничего не найдено'} loadingText="Загрузка…" clearText="Сбросить"
    />
  );
};

interface PharmacyProps {
  options: JournalPharmacy[];
  value: string[];
  onChange: (guids: string[]) => void;
  max: number;
  minWidth?: number;
  maxWidth?: number;
  fullWidth?: boolean;
}

const pharmacySearch = createFilterOptions<JournalPharmacy>({
  stringify: p => `${p.num} ${p.name} ${p.region || ''}`
});

/** Мультивыбор аптек зоны: номер, регион и число записей в журнале */
export const PharmacyFilter: React.FC<PharmacyProps> = ({ options, value, onChange, max, minWidth = 240, maxWidth = 420, fullWidth }) => {
  const byGuid = useMemo(() => {
    const map: Record<string, JournalPharmacy> = {};
    options.forEach(o => { map[o.guid] = o; });
    return map;
  }, [options]);
  const selected = useMemo(
    () => value.map(g => byGuid[g]).filter((o): o is JournalPharmacy => Boolean(o)),
    [value, byGuid]
  );

  return (
    <Autocomplete<JournalPharmacy, true>
      multiple size="small" limitTags={1} disableCloseOnSelect fullWidth={fullWidth}
      sx={{ minWidth, maxWidth: fullWidth ? undefined : maxWidth }}
      options={options} value={selected} filterOptions={pharmacySearch}
      onChange={(_, v) => onChange(v.slice(0, max).map(o => o.guid))}
      getOptionLabel={o => o.name}
      isOptionEqualToValue={(a, b) => a.guid === b.guid}
      renderOption={(props, o, { selected: checked }) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
        return (
          <li key={key} {...rest}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: checked ? 700 : 500 }} noWrap>{o.name}</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }} noWrap>
                  {o.region || 'Регион не указан'}{o.active ? '' : ' · нет активной учётки'}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.7rem', color: o.records ? 'text.secondary' : '#dc2626', flexShrink: 0 }}>
                {o.records ? `${o.records.toLocaleString('ru-RU')} уп.` : 'нет записей'}
              </Typography>
            </Box>
          </li>
        );
      }}
      renderInput={params => (
        <TextField {...params} label="Аптеки" placeholder={selected.length ? '' : 'Все аптеки зоны'} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />
      )}
      noOptionsText="Аптека не найдена" clearText="Сбросить"
    />
  );
};
